import Pusher from "pusher-js";
import { debounce } from "lodash";
import ConfigService from "./ConfigService";

/**
 * Delay before disconnecting when there are no more active event bindings, in milliseconds.
 */
const DISCONNECT_DEBOUNCE_MS = 60 * 1000;

/**
 * Timeout after which to stop waiting, thereby rejecting the returned promise, in milliseconds.
 */
const SEQUENCE_TIMEOUT_MS = 60 * 1000;

const PUSHER_CLUSTER = "eu";

/**
 * Allows for receiving push events from the server.
 * This is currently implemented using Pusher.
 * A connection is opened only when needed, i.e. while specific events are actively bound to.
 */
class PushService {
	constructor() {
		this.pusher = null;
		this.subscriptionMap = new Map();
		this.disconnectDebounced = debounce(
			this.disconnect.bind(this),
			DISCONNECT_DEBOUNCE_MS
		);
	}

	/**
	 * Register a function to be called when events of a given name occur.
	 * To keep this implementation agnostic, a single event name is used.
	 * In the case of the current Pusher implementation, this should be in the format "<channel name>/<event name>".
	 *
	 * @param {string} name - The event path in format "channelName/eventName"
	 * @param {Function} handler - The function to call when the event occurs
	 * @returns {Function} An unbinder function that can be called to remove the binding
	 */
	bind(name, handler) {
		const [channelName, eventName] = name.split("/");
		if (!channelName || !eventName) {
			console.error(`Invalid event name "${name}"!`);
			return () => {};
		}

		let subscription = this.subscriptionMap.get(channelName);
		if (!subscription) {
			const pusher = this.connect();
			subscription = new Subscription(pusher.subscribe(channelName));
			this.subscriptionMap.set(channelName, subscription);
		}

		const binding = subscription.bind(eventName, handler);

		return () => this.doUnbind(channelName, eventName, handler, binding);
	}

	/**
	 * Convenience method to call (multiple) unbinder functions as returned by bind.
	 *
	 * @param {Function|Array<Function>} unbinders - A single unbinder or array of unbinders to call
	 */
	unbind(unbinders) {
		if (Array.isArray(unbinders)) {
			unbinders.forEach((unbinder) => unbinder());
		} else if (typeof unbinders === "function") {
			unbinders();
		} else {
			console.warn("unbind called with something other than function(s)");
		}
	}

	/**
	 * Convenience method for handling a finite sequence of events.
	 * Binds and then automatically unbinds once the sequence completes.
	 * Each event received is passed to a handler function that decides when to stop
	 * the sequence by calling one of the passed in resolve or reject functions.
	 *
	 * @param {string} name - Event name in format "channelName/eventName"
	 * @param {Function} handler - Handler for sequence of events
	 * @param {number} timeout - Timeout in milliseconds
	 * @returns {Promise} A promise that resolves when the sequence is complete
	 */
	sequence(
		name,
		handler = (event, resolve) => resolve(event),
		timeout = SEQUENCE_TIMEOUT_MS
	) {
		let unbind;
		let tid;
		return new Promise((resolve, reject) => {
			unbind = this.bind(name, (event) => handler(event, resolve, reject));
			tid = setTimeout(() => reject("timeout"), timeout);
		}).finally(() => {
			unbind();
			clearTimeout(tid);
		});
	}

	/**
	 * Internal method to handle unbinding events
	 */
	doUnbind(channelName, eventName, handler, binding) {
		if (!this.pusher) {
			console.warn("not connected");
			return;
		}

		const subscription = this.subscriptionMap.get(channelName);
		if (!subscription) {
			console.warn(
				`no subscription for "${channelName}" with event "${eventName}"`
			);
			return;
		}

		subscription.unbind(eventName, handler, binding);

		if (subscription.bindings.size === 0) {
			this.pusher.unsubscribe(channelName);
			this.subscriptionMap.delete(channelName);
			this.disconnectDebounced();
		}
	}

	/**
	 * Connect to Pusher if not already connected
	 */
	connect() {
		if (!this.pusher) {
			const appKey = ConfigService.get("PUSHER_APP_KEY");
			if (!appKey) {
				console.warn("Pusher not configured - missing app key");
			}

			this.pusher = new Pusher(appKey, {
				cluster: PUSHER_CLUSTER,
			});

			this.pusher.connection.bind("error", (error) => {
				console.error("Pusher connection error:", error);
			});
		}
		return this.pusher;
	}

	/**
	 * Disconnect from Pusher if there are no active subscriptions
	 */
	disconnect() {
		if (this.pusher && this.subscriptionMap.size === 0) {
			this.pusher.disconnect();
			this.pusher = null;
		}
	}
}

/**
 * Internal supporting class for managing channel subscriptions and bindings
 */
class Subscription {
	constructor(channel) {
		this.channel = channel;
		this.bindings = new Set();
	}

	/**
	 * Bind an event handler to this channel
	 *
	 * @param {string} eventName - The event name to listen for
	 * @param {Function} handler - The handler function to call
	 * @returns {Symbol} A unique binding identifier
	 */
	bind(eventName, handler) {
		this.channel.bind(eventName, handler);
		const binding = Symbol("binding");
		this.bindings.add(binding);
		return binding;
	}

	/**
	 * Unbind an event handler from this channel
	 *
	 * @param {string} eventName - The event name to unbind
	 * @param {Function} handler - The handler to unbind
	 * @param {Symbol} binding - The binding identifier
	 */
	unbind(eventName, handler, binding) {
		if (this.bindings.has(binding)) {
			this.channel.unbind(eventName, handler);
			this.bindings.delete(binding);
		} else {
			console.warn(
				`Unknown event binding for "${eventName}"! Maybe already unbound?`
			);
		}
	}
}

const pushService = new PushService();
export default pushService;
